import type { HealthLevel, HealthStripCell, MaintenanceState } from '../types';

export interface LevelMeta {
  label: string;
  color: string; // ant-design tag color
  order: number;
}

const LEVELS: Record<HealthLevel, LevelMeta> = {
  critical: { label: '异常', color: 'red', order: 0 },
  attention: { label: '需关注', color: 'orange', order: 1 },
  ok: { label: '正常', color: 'green', order: 2 },
};

export const levelMeta = (level: HealthLevel): LevelMeta => LEVELS[level] ?? LEVELS.ok;

export const formatAge = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds} 秒前`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} 分钟前`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} 小时前`;
  return `${Math.floor(seconds / 86400)} 天前`;
};

export const formatUptime = (percent: number | null | undefined): string =>
  percent == null ? '—' : `${Number(percent.toFixed(1))}%`;

export type UptimeTone = 'none' | 'good' | 'warn' | 'bad';

export const uptimeTone = (percent: number | null | undefined): UptimeTone => {
  if (percent == null) return 'none';
  if (percent >= 99) return 'good';
  if (percent >= 95) return 'warn';
  return 'bad';
};

export const STRIP_NO_DATA = '#e5e7eb';

export const stripCellColor = (cell: HealthStripCell | null): string => {
  if (!cell) return STRIP_NO_DATA;
  if (cell.o < 0.5) return '#ff4d4f';
  if (cell.o < 0.95) return '#faad14';
  if (cell.x > 0) return '#faad14';
  if (cell.c >= 85) return '#fadb14';
  return '#52c41a';
};

// The strip's last cell is the hour containing `generatedAt`.
export const stripCellHour = (generatedAt: string, index: number, total: number): Date => {
  const current = new Date(generatedAt);
  current.setMinutes(0, 0, 0);
  return new Date(current.getTime() - (total - 1 - index) * 3600_000);
};

const two = (n: number) => String(n).padStart(2, '0');

export const stripCellTitle = (cell: HealthStripCell | null, hour: Date): string => {
  const stamp = `${two(hour.getMonth() + 1)}-${two(hour.getDate())} ${two(hour.getHours())}:00`;
  if (!cell) return `${stamp} 无记录`;
  const parts = [`在线 ${Math.round(cell.o * 100)}%`, `CPU ${Math.round(cell.c)}%`, `内存 ${Math.round(cell.m)}%`];
  if (cell.x > 0) parts.push(`缺失进程 ${cell.x}`);
  return `${stamp} ${parts.join(' · ')}`;
};

// The game's schedule is in China time whatever the browser's zone is.
const CHINA = 'Asia/Shanghai';
const clock = (iso: string) =>
  new Intl.DateTimeFormat('zh-CN', { timeZone: CHINA, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso));
const weekday = (iso: string) =>
  new Intl.DateTimeFormat('zh-CN', { timeZone: CHINA, weekday: 'short' }).format(new Date(iso)).replace('星期', '周');

export const formatWhen = (iso: string): string => `${weekday(iso)} ${clock(iso)}`;

export interface MaintenanceText {
  title: string;
  detail: string;
}

export const maintenanceText = (state: MaintenanceState): MaintenanceText => {
  if (state.open && state.source === 'manual_open' && state.closes_at) {
    return { title: '已手动开启维护窗口', detail: `将在 ${clock(state.closes_at)} 自动关闭` };
  }
  if (state.open) {
    const until = state.closes_at ? `预计 ${clock(state.closes_at)} 自动关闭；` : '';
    return { title: '维护窗口已开启', detail: `${until}开服后请手动关闭窗口` };
  }
  const next = `下次开启 ${formatWhen(state.next_opens_at)}`;
  if (state.source === 'manual_closed') return { title: '维护窗口已关闭', detail: `已手动关闭（开服）；${next}` };
  return { title: '当前不在维护窗口', detail: next };
};
